import { SearchInputOption } from '@shared/components/search-input/SearchInput';
import { useGetPlacesQuery } from '@shared/generated/graphql';
import { placesFromFragment } from '@shared/graphql/fromFragments/place';
import { Place } from '@shared/types/place';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { PiBuildings } from 'react-icons/pi';

export const SAVED_PLACE_PREFIX = 'saved:';

type UseSavedPlacesSuggestionsOptions = {
  organizationId?: string;
  originatorId?: string;
};

type UseSavedPlacesSuggestionsResult = {
  options: SearchInputOption[];
  places: Place[];
  setSearchTerm: (term: string) => void;
  loading: boolean;
};

export function useSavedPlacesSuggestions({
  organizationId,
  originatorId,
}: UseSavedPlacesSuggestionsOptions): UseSavedPlacesSuggestionsResult {
  const [searchTerm, setSearchTermRaw] = useState('');

  const setSearchTerm = useMemo(() => debounce(setSearchTermRaw, 75), []);

  const hasRequiredContext = Boolean(organizationId && originatorId);

  const { data, loading } = useGetPlacesQuery({
    variables: {
      input: {
        organizationId: organizationId ?? '',
        originatorIds: originatorId ? [originatorId] : undefined,
        searchTerm: searchTerm || undefined,
      },
      first: 10,
      sort: { field: 'place.updatedAt', order: 'DESC' },
    },
    skip: !hasRequiredContext || !searchTerm,
  });

  const places = useMemo(() => (data ? placesFromFragment(data) : []), [data]);

  const options: SearchInputOption[] = useMemo(
    () =>
      places.map((place) => ({
        label: formatPlaceLabel(place),
        details: formatPlaceDetails(place),
        id: `${SAVED_PLACE_PREFIX}${place.id}`,
        icon: <PiBuildings />,
      })),
    [places]
  );

  return { options, places, setSearchTerm, loading };
}

function formatPlaceLabel(place: Place): string {
  const name = place.name || 'Unknown location';
  if (place.locationCode) {
    return `${place.locationCode} - ${name}`;
  }
  return name;
}

function formatPlaceDetails(place: Place): string {
  const { city, state, zip } = place.address ?? {};
  const parts = [city, state].filter(Boolean);
  const line = parts.join(', ');
  return zip ? `${line} ${zip}` : line;
}
